import { useCallback, useState, FC } from "react";

import { Link } from "react-router-dom";

import { DateTime } from "luxon";
import { Button as ButtonPrime } from "primereact/button";

import "@/assets/scss/DataGrid.scss";
import { iconRoomMapping } from "@/_helpers/IconMapping.ts";
import calculus from "@/assets/svg/calculus.svg";
import house from "@/assets/svg/house.svg";
import Key from "@/assets/svg/Key";
import smokeDetector from "@/assets/svg/smokeDetector.svg";
import { ButtonFile } from "@/common/components/ButtonFile.tsx";
import {
  OperatingStatus,
  OperatingStatusKeys,
  Permissions,
  Status,
} from "@/common/enums/sharedEnums.ts";
import { DwellingCostingsModal } from "@/modules/dwelling/components/sheet/DwellingCostingsModal.tsx";
import DwellingInventoriesModal from "@/modules/dwelling/components/sheet/DwellingInventoriesModal.tsx";
import { PercentagesTotalCondition } from "@/modules/dwelling/components/sheet/Percentages.tsx";
import styles from "@/modules/dwelling/containers/DwellingEstimateContainer/DwellingEstimateContainer.module.scss";
import { TagChart } from "@/modules/dwelling/containers/DwellingEstimateContainer/TagChart";

import DwellingFeatureTable from "./DwellingFeatureTable";
import DwellingHighlight from "./DwellingHighlight";
import { DwellingStatsTable } from "./DwellingStatsTable.tsx";
import MetersTileComponent from "./MetersTileComponent";
import {
  ServiceReport,
  Unit,
} from "../../../serviceOrdering/interfaces/ServiceReport.ts";
import { DWELLING_DETAILS_ESTIMATE } from "../../routes/index.ts";

export interface DwellingLastInventoryComponentProps {
  dwellingId: string;
  report: ServiceReport;
}

const DwellingLastInventory: FC<DwellingLastInventoryComponentProps> = ({
  dwellingId,
  report,
}) => {
  const wordCheckSmokeDetector = "fumée";
  const [visibleRows, setVisibleRows] = useState(3);
  const [visibleRowsEquipment, setVisibleRowsEquipment] = useState(false);

  const getSmokeDetectorData = useCallback(() => {
    return report?.equipment?.units?.filter((unit: Unit) =>
      unit.name.includes(wordCheckSmokeDetector)
    );
  }, [report]);

  const ObservationHandler = () => {
    const data = report.observations.map((obs) => {
      return { title: obs.name, state: obs.value };
    });
    const size = report.observations.length;
    const half = Math.floor(size / 2);
    const showMoreClickHandler = () => {
      setVisibleRows(
        (prevVisibleRows) =>
          prevVisibleRows + data.slice(0, half).length - prevVisibleRows
      );
    };

    return (
      <div className="table-container">
        <div className="d-flex gap-3">
          <DwellingFeatureTable
            rows={data.slice(0, half)}
            visibleRows={visibleRows}
          />
          <DwellingFeatureTable
            rows={data.slice(half)}
            visibleRows={visibleRows}
          />
        </div>
        {data.slice(0, half).length > visibleRows && (
          <div className="button-container">
            <ButtonPrime
              className="button-link"
              label="Afficher plus"
              link
              icon="pi pi-angle-down"
              iconPos="right"
              onClick={showMoreClickHandler}
            ></ButtonPrime>
          </div>
        )}
      </div>
    );
  };

  const EquipmentHandler = () => {
    const equipmentData = report?.equipment?.units.filter(
      (equipment) => !equipment.name.includes(wordCheckSmokeDetector)
    );

    return (
      <>
        {equipmentData?.length > 0 ? (
          !visibleRowsEquipment ? (
            <div className="button-container">
              <ButtonPrime
                className="button-link"
                label="Afficher plus"
                link
                icon="pi pi-angle-down"
                iconPos="right"
                onClick={() => setVisibleRowsEquipment(true)}
              ></ButtonPrime>
            </div>
          ) : (
            equipmentData.map((equipment, index) => {
              return (
                <div className="px-2 col-lg-6" key={index}>
                  <DwellingHighlight
                    key={index}
                    title={equipment.name}
                    icon={
                      <img
                        src={house}
                        alt={equipment.name}
                        style={{
                          width: "35px",
                          height: "35px",
                        }}
                      />
                    }
                    link={
                      equipment.photos && equipment.photos[0]
                        ? equipment.photos[0].path
                        : undefined
                    }
                    data={[
                      { title: "Nombre", value: equipment.number },
                      {
                        title: "Emplacement",
                        value: equipment.location ?? "-",
                      },
                      { title: "État", value: Status[equipment.status] || "-" },
                      {
                        title: "État de marche",
                        value:
                          OperatingStatus[equipment.operatingStatus] || "-",
                      },
                    ]}
                  />
                </div>
              );
            })
          )
        ) : null}
      </>
    );
  };

  const getKeyNumber = (units: Unit[], status: string) => {
    let number = 0;
    const keys = units.filter((v: any) => v.operatingStatus === status) ?? [];
    if (keys.length > 0) {
      keys.map((v: any) => {
        number += v.number;
      });
    }
    return number;
  };

  const getKeyTotalNumber = (units: Unit[]) => {
    let number = 0;
    if (units.length > 0) {
      units.map((v: any) => {
        number += v.number;
      });
    }
    return number;
  };

  return (
    <div className="d-flex flex-column gap-3">
      <div className="bg-container d-flex flex-column gap-4">
        <div className="dwelling_title d-flex justify-content-between">
          Liste des pièces
        </div>
        <div className="dwelling_rooms">
          {report?.rooms?.map((room, index) => {
            const IconPath = iconRoomMapping[room.category];
            return (
              <div key={index} className="dwelling_room">
                {IconPath && <img src={IconPath} alt={room.category} />}
                <span>{room.name}</span>
              </div>
            );
          })}
        </div>
      </div>
      <div className="bg-container d-flex flex-column gap-4">
        <div className="d-flex justify-content-between align-items-center">
          <span className="dwelling_title">
            Dernier état des lieux
            <span>
              - ({DateTime.fromISO(report.updatedAt).toFormat("dd/MM/yyyy")})
            </span>
            <DwellingInventoriesModal dwellingId={dwellingId} />
          </span>
          <div>
            {report?.permissions?.includes(Permissions.SUPER_ADMIN_ACTION) && (
              <>
                <Link
                  to={DWELLING_DETAILS_ESTIMATE.replace(":id", dwellingId)}
                  className={`btn btn-success me-2 ${
                    !report?.service?.canDoCosting ? "disabled" : ""
                  }`}
                  style={{ height: "36px" }}
                >
                  <img
                    src={calculus}
                    alt="calcul"
                    width={"15px"}
                    className="me-2"
                  />
                  Chiffrage
                </Link>
                <DwellingCostingsModal />
              </>
            )}
          </div>
        </div>
        <div className="condition d-lg-flex justify-content-between">
          <PercentagesTotalCondition
            width={"65%"}
            totalCondition={report?.totalCondition}
          />
          <div className={styles.tagChartContainer}>
            <TagChart
              mainValue={`${report?.totalGoodCondition}%`}
              label="Bon état et +"
            />
            <TagChart
              mainValue={`${report?.totalCleanliness?.clean?.percentage}%`}
              label="Indice propreté"
            />
          </div>
        </div>

        <DwellingStatsTable report={report} />

        <div className="propertySheetDetailsPartSubtitle">
          Observations constatées
        </div>
        {report?.observations?.length > 0 ? (
          <ObservationHandler />
        ) : (
          <span>Aucune observations constatées.</span>
        )}
        <div className="d-flex">
          <ButtonFile
            path={report?.mainFile}
            label="Voir l'état des lieux au complet"
            isDisabled={
              report?.mainFile === undefined || report?.mainFile === ""
            }
          />
        </div>
        <div className="row">
          {/* clés */}
          <div className="px-2 col-lg-6">
            {report.key ? (
              <DwellingHighlight
                title="Les clés"
                icon={<Key color="#641abf" w="27" />}
                link={report.key?.photo}
                data={[
                  {
                    title: "Nombre de clés",
                    /*value: report.key.nbUnits,*/
                    value: getKeyTotalNumber(report.key.units),
                  },
                  {
                    title: "Fonctionnel",
                    value: getKeyNumber(
                      report.key.units,
                      OperatingStatusKeys.F
                    ),
                  },
                  {
                    title: "Non Fonctionnel",
                    value: getKeyNumber(
                      report.key.units,
                      OperatingStatusKeys.NF
                    ),
                  },
                  {
                    title: "Non vérifié",
                    value: getKeyNumber(
                      report.key.units,
                      OperatingStatusKeys.NV
                    ),
                  },
                ]}
              />
            ) : (
              <></>
            )}
          </div>

          {/* Détecteur de fumée */}
          {getSmokeDetectorData()?.map((smokeDetectorData, index) => {
            return (
              <div className="px-2 col-lg-6" key={index}>
                <DwellingHighlight
                  title={smokeDetectorData.name}
                  icon={<img src={smokeDetector} alt="Détecteur de fumée" />}
                  link={
                    smokeDetectorData.photos && smokeDetectorData.photos[0]
                      ? smokeDetectorData.photos[0].path
                      : undefined
                  }
                  data={[
                    {
                      title: "Présence",
                      value: smokeDetectorData.number > 0 ? "Oui" : "Non",
                    },
                    {
                      title: "Emplacement",
                      value: smokeDetectorData.location ?? "-",
                    },
                    {
                      title: "État",
                      value: Status[smokeDetectorData.status] || "-",
                    },
                    {
                      title: "État de marche",
                      value:
                        OperatingStatus[smokeDetectorData.operatingStatus] ||
                        "-",
                    },
                  ]}
                />
              </div>
            );
          })}

          {/* equipments */}
          <EquipmentHandler />
        </div>
      </div>
      <div className="bg-container d-flex flex-column gap-4">
        <div className="d-flex justify-content-between">
          <span className="dwelling_title">
            Relevé de compteurs
            <span>
              - ({DateTime.fromISO(report.updatedAt).toFormat("dd/MM/yyyy")})
            </span>
          </span>
        </div>
        <MetersTileComponent meters={report.meters} />
      </div>
    </div>
  );
};

export default DwellingLastInventory;
